import React, { useEffect, useState } from "react";
import { Download, X, PawPrint } from "lucide-react";
import { track } from "../lib/analytics";

const DISMISS_KEY = "pg_pwa_install_dismissed_at";
const DISMISS_DAYS = 14; // re-prompt after two weeks if user dismissed

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  (typeof window !== "undefined" &&
    window.matchMedia?.("(display-mode: standalone)")?.matches) ||
  // iOS Safari
  (typeof navigator !== "undefined" && navigator.standalone === true);

const wasDismissedRecently = () => {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

// `?showInstall=1` override — bypasses both the standalone check and the
// 14-day dismissal cookie so QA / the team can verify the banner on any
// device without clearing site data. The first call to this also clears
// the dismissal cookie so subsequent loads (in the same browser) keep
// showing the banner normally.
const isForceShow = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("showInstall") === "1") {
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      return true;
    }
  } catch { /* ignore */ }
  return false;
};

/**
 * InstallPrompt
 * -------------
 * On Android/desktop Chrome, captures the `beforeinstallprompt` event and
 * shows a subtle bottom banner with an Install button that triggers the
 * native OS install dialog.
 *
 * On iOS (where the API doesn't exist), shows a Safari-specific hint with
 * the Share icon instructions since that's the only way to install there.
 *
 * Respects dismissal for 14 days and never shows when already installed.
 */
const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const force = isForceShow();
    if (!force) {
      if (isStandalone()) return;
      if (wasDismissedRecently()) return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Small delay so we don't flash the banner on first paint.
      setTimeout(() => setVisible(true), 1500);
      track("pwa_prompt_shown", { platform: "android_or_desktop" });
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      track("pwa_installed");
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback: show the hint banner after a delay if nothing else fires.
    // Force-show also routes through here so QA can preview the banner on any
    // device when `beforeinstallprompt` hasn't fired yet.
    let iosTimer;
    if (isIOS() || force) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
        track("pwa_prompt_shown", { platform: force ? "force_qa" : "ios" });
      }, force ? 500 : 3000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* ignore */ }
    track("pwa_prompt_dismissed", { platform: iosHint ? "ios" : "android_or_desktop" });
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      track("pwa_prompt_result", { outcome });
      if (outcome === "accepted") setVisible(false);
    } catch { /* ignore */ }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed left-4 right-4 bottom-20 md:bottom-6 md:left-auto md:right-6 md:max-w-sm z-[10000] animate-fade-in-up"
    >
      <div className="bg-white border border-brand-border-warm rounded-2xl shadow-warm-lg p-4 flex items-start gap-3">
        <span className="shrink-0 w-10 h-10 rounded-xl bg-brand-forest text-white flex items-center justify-center">
          <PawPrint className="w-5 h-5" strokeWidth={2.4} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base font-bold text-brand-ink leading-tight">
            Install Pawground
          </div>
          {iosHint ? (
            <div className="text-sm text-brand-ink/70 mt-1 leading-snug">
              Tap the <span className="font-semibold">Share</span> icon, then
              choose <span className="font-semibold">Add to Home Screen</span>.
            </div>
          ) : (
            <div className="text-sm text-brand-ink/70 mt-1 leading-snug">
              Get one-tap check-ins and park photos right from your home screen.
            </div>
          )}
          {!iosHint && (
            <button
              type="button"
              data-testid="pwa-install-btn"
              onClick={install}
              className="mt-3 inline-flex items-center gap-2 bg-brand-forest text-white text-sm font-semibold rounded-full px-4 py-2 hover:bg-brand-forest-dark transition-colors active:scale-95"
            >
              <Download className="w-4 h-4" />
              Install app
            </button>
          )}
        </div>
        <button
          type="button"
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss install prompt"
          onClick={dismiss}
          className="shrink-0 w-8 h-8 rounded-full hover:bg-brand-cream text-brand-ink/50 hover:text-brand-ink flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
